import type { Element as HastElement } from "hast";

export interface SpanNodeCarrier {
    readonly node?: HastElement;
}

/**
 * What the node RENDERS, with nothing that merely describes where it came from.
 *
 * Hast attaches source `position` offsets to every parsed node, including
 * inside `children`, not only at the top level — so a shape that serializes a
 * node wholesale changes whenever an unrelated edit shifts position, even
 * when the rendered content is identical.
 */
const renderShape = (
    node: HastElement | HastElement["children"][number]
): unknown => {
    if (node.type === "text") return ["#text", node.value];
    if (node.type !== "element") return [node.type];
    return [
        node.tagName,
        node.properties ?? {},
        (node.children ?? []).map(renderShape),
    ];
};

export function spanRenderKey(
    node: HastElement | undefined
): string | undefined {
    if (!node) return undefined;

    return JSON.stringify([
        node.properties ?? {},
        (node.children ?? []).map(renderShape),
    ]);
}

/**
 * The fence `memoizeMarkdownComponents` would have provided, kept by hand.
 *
 * react-markdown re-parses on every streaming frame, so a span's node,
 * `children` and `properties` are all FRESH OBJECTS each frame even when
 * nothing about it changed. Measured — a reference comparison reports
 * "changed" every frame and re-renders every span in the message; a content
 * comparison reports "same" and does not. So this compares content.
 * Reference identity is kept as the cheap first answer for the case where
 * the node genuinely is the same one.
 *
 * The memoizer also strips `position`/`data` from `properties` before comparing;
 * that is a no-op and is not copied here. Both are NODE-level fields in hast and
 * never appear inside `properties` — verified against a parsed span, whose keys
 * are `type, tagName, properties, children, position`.
 *
 * Provenance is invisible to this comparison, and safely so: the reserved
 * `dataEditorialGlyph` attribute alone would not distinguish a minted glyph
 * from a forged one, since authoring it in markdown is exactly the attack the
 * provenance gate exists to defeat. What actually separates them is upstream:
 * `rehypeMarkEditorialStructure` scrubs the reserved namespace from every
 * unproven node, so a forgery reaches this comparison with the attribute
 * already gone. Measured — a forged span arrives with no `dataEditorialGlyph`
 * at all.
 *
 * That matters to anyone changing this file: the safety here is BORROWED from
 * the scrub, not local. A render path that skipped the structural pass would
 * hand this function a proven and an unproven span that compare equal.
 */
export function areSpanNodesEqual(
    prev: SpanNodeCarrier,
    next: SpanNodeCarrier
): boolean {
    if (prev.node === next.node) return true;
    if (!prev.node || !next.node) return false;

    return spanRenderKey(prev.node) === spanRenderKey(next.node);
}
